import React, { useEffect, useState } from 'react';
import { PortalTab } from '../../types';

interface Village {
  id: string;
  name: string;
  slug: string;
  dauduOrLeader: string;
  leaderTitle?: string;
  populationEstimate: string;
  culturalSignificance: string;
  activeTroupe: string;
  displayOrder: number;
}

interface Props {
  slug?: string;
  setActiveTab: (tab: PortalTab) => void;
}

const COMMUNITY_ICONS: Record<string, string> = {
  'jattu':     '👑', 'afashio':   '🪘', 'afowa':     '🌳',
  'apana':     '🎣', 'ayaoghena': '🕯️', 'ayogwiri':  '⚔️',
  'ayua':      '🌿', 'elele':     '🌾', 'idatto':    '🏺',
  'ikabigbo':  '🤼', 'ikholo':    '🕊️', 'imeke':     '🧵',
  'imhonikhe': '🛕', 'irekpai':   '⚒️', 'iyamho':    '🎓',
  'iyora':     '🎭', 'iyuku':     '🏪', 'ogbido':    '🌱',
  'ozor':      '🏹', 'ugbenor':   '🌴', 'uluoke':    '⛰️',
  'yelwa':     '🤝',
};

const getCommunityIcon = (slug: string): string =>
  COMMUNITY_ICONS[slug.split('-')[0]] || '🏘️';

const CULTURAL_DATA: Record<string, {
  specialty: string;
  highlights: string[];
  festivalNote: string;
  landmarks: string[];
  color: string;
}> = {
  'jattu-uzairue':     { specialty: 'Royal administration and ceremonial capital', highlights: ['Seat of the Ogieneni', 'Ceremonial capital of Uzairue Kingdom', 'Palace of the Ogieneni'], festivalNote: 'Jattu-Uzairue hosts the grand opening and closing ceremonies of the ERUE Festival at the Palace of the Ogieneni.', landmarks: ['Palace of the Ogieneni', 'Jattu Central Market', 'Jattu Junction'], color: '#0F5132' },
  'afashio-uzairue':   { specialty: 'Age-grade heritage and traditional craftsmanship', highlights: ['Egbele age-grade heritage', 'Traditional craftsmanship', 'Communal governance'], festivalNote: 'Afashio artisans display handcrafted works at the ERUE Festival cultural market.', landmarks: ['Afashio Community Hall', 'Traditional Craft Centre'], color: '#1a6b3f' },
  'afowa-uzairue':     { specialty: 'Founding settlement traditions and oral history', highlights: ['Founding settlement of Uzairue', 'Ancient covenant memory', 'Iroko grove traditions'], festivalNote: 'Afowa elders contribute foundational oral history recitations during the ERUE Festival heritage programme.', landmarks: ['Sacred Iroko Grove', 'Ancient Covenant Shrine'], color: '#145f35' },
  'apana-uzairue':     { specialty: 'River heritage and artisanal fishing', highlights: ['River and water heritage', 'Artisanal fishing rites', 'Water thanksgiving ceremonies'], festivalNote: 'The Apana Road Race is a celebrated sporting highlight of the Annual ERUE Festival.', landmarks: ['Apana River', 'Fishing Heritage Grounds', 'Apana Road Race Route'], color: '#0e5c3a' },
  'ayaoghena-uzairue': { specialty: 'Ancestral pathway traditions and memorial rites', highlights: ['Ancestral pathway traditions', 'Memorial masquerades', 'Heritage of departed heroes'], festivalNote: "Ayaoghena's remembrance masquerades feature prominently in the ERUE Festival heritage evening.", landmarks: ['Ancestral Memorial Ground', 'Remembrance Masquerade Arena'], color: '#1a6b3f' },
  'ayogwiri-uzairue':  { specialty: 'Warrior heritage and traditional wrestling', highlights: ['Warrior heritage', 'Martial age-grade traditions', 'Traditional wrestling'], festivalNote: 'Ayogwiri wrestlers and warrior dancers are featured performers at the ERUE Festival cultural displays.', landmarks: ['Historic Warrior Shrine', 'Traditional Wrestling Arena'], color: '#145f35' },
  'ayua-uzairue':      { specialty: 'Spiritual authority and traditional medicine', highlights: ['Spiritual authority', 'Traditional medicine and herbalism', 'Ritual specialists'], festivalNote: 'Ayua herbalists contribute to the ERUE Festival wellness and cultural knowledge exhibitions.', landmarks: ['Healing Herb Garden', 'Ritual Specialist Compound'], color: '#0e5c3a' },
  'elele-uzairue':     { specialty: 'Agricultural heritage and New Yam traditions', highlights: ['Yam cultivation heritage', 'New Yam / Iko Ebo tradition', 'Communal farm heritage'], festivalNote: "Elele's New Yam traditions are celebrated during the ERUE Festival agricultural and cultural programme.", landmarks: ['Communal Yam Farm', 'New Yam Festival Grounds'], color: '#1a6b3f' },
  'idatto-uzairue':    { specialty: 'Pottery and clay artisanship', highlights: ['Pottery heritage', 'Women-led craft tradition', 'Ceremonial clayware'], festivalNote: 'Idatto potters display their craft at the ERUE Festival artisan market.', landmarks: ['Pottery Workshop Quarter', 'Clay Artisans Market'], color: '#145f35' },
  'ikabigbo-uzairue':  { specialty: 'Traditional wrestling and wood carving', highlights: ['Traditional wrestling / Ovia', 'Wood carving heritage', 'Masquerade masks'], festivalNote: 'Ikabigbo wrestlers and carvers are among the most anticipated performers at the ERUE Festival.', landmarks: ['Wrestling Heritage Ground', 'Wood Carvers Quarter'], color: '#0e5c3a' },
  'ikholo-uzairue':    { specialty: 'Mediation and peacemaking traditions', highlights: ['Mediation heritage', 'Elder council traditions', 'Trade facilitation'], festivalNote: 'Ikholo elders play an honoured role in the ceremonial protocols of the ERUE Festival.', landmarks: ['Elder Council Hall', 'Community Mediation Centre'], color: '#1a6b3f' },
  'imeke-uzairue':     { specialty: 'Master weaving and ceremonial textiles', highlights: ['Master weaving tradition', 'Etsako ceremonial textiles', 'Royal cloth production'], festivalNote: 'Imeke weavers produce ceremonial textiles worn by dignitaries during the ERUE Festival royal proceedings.', landmarks: ['Royal Weaving Hall', 'Textile Heritage Centre'], color: '#145f35' },
  'imhonikhe-uzairue': { specialty: 'Ancestral shrine custodianship and ritual consecration', highlights: ['Ancestral shrine custodians', 'Odion elder authority', 'Ritual consecration traditions'], festivalNote: 'Imhonikhe shrine custodians provide ceremonial blessings for the ERUE Festival.', landmarks: ['Ancestral Shrine Complex', 'Odion Council Ground'], color: '#0e5c3a' },
  'irekpai-uzairue':   { specialty: 'Blacksmithing and iron working', highlights: ['Blacksmithing heritage', 'Iron working tradition', 'Farm tools and hunting implements'], festivalNote: 'Irekpai blacksmiths demonstrate traditional iron-working craft at the ERUE Festival artisan showcase.', landmarks: ['Old Forge Site', 'Blacksmith Quarter'], color: '#1a6b3f' },
  'iyamho-uzairue':    { specialty: 'Education and intellectual heritage', highlights: ['Education and intellectual heritage', 'Academic and professional prominence', 'University and educational corridor'], festivalNote: 'Iyamho scholars and professionals contribute to the ERUE Festival lecture series.', landmarks: ['Iyamho Educational Corridor', 'Academic Heritage Centre'], color: '#145f35' },
  'iyora-uzairue':     { specialty: 'Ekaba Festival custodianship and sacred masquerade traditions', highlights: ['Ekaba Festival heritage', 'Sacred masquerade tradition', 'Communal renewal rites'], festivalNote: 'As custodian of the Ekaba Festival, Iyora-Uzairue contributes one of the most spiritually significant performances of the Annual ERUE Festival.', landmarks: ['Sacred Grove', 'Ekaba Festival Grounds', 'Ritual Dance Arena'], color: '#0e5c3a' },
  'iyuku-uzairue':     { specialty: 'Market and trade heritage', highlights: ['Commercial crossroads', 'Periodic market traditions', 'Regional produce exchange'], festivalNote: 'Iyuku hosts a dedicated trade and commerce exhibition at the Annual ERUE Festival.', landmarks: ['Iyuku Periodic Market', 'Trade Crossroads Junction'], color: '#1a6b3f' },
  'ogbido-uzairue':    { specialty: 'Pottery and herbal medicine', highlights: ['Pottery and storage vessels', 'Herbal medicine tradition', 'Forest stewardship'], festivalNote: 'Ogbido herbalists and potters present demonstrations at the ERUE Festival wellness exhibitions.', landmarks: ['Forest Medicine Garden', 'Pottery Production Grounds'], color: '#145f35' },
  'ozor-uzairue':      { specialty: "Hunters' guild and forest heritage", highlights: ["Hunters' Guild / Otele traditions", 'Forest knowledge and tracking', "Hunters' ceremonial dance"], festivalNote: "The Ozor hunters' ceremonial dance is a crowd-favourite display at the Annual ERUE Festival.", landmarks: ["Hunters' Guild Ground", 'Forest Heritage Trail'], color: '#0e5c3a' },
  'ugbenor-uzairue':   { specialty: 'Palm oil production and women-led cooperatives', highlights: ['Palm oil production heritage', 'Palm kernel processing', 'Women-led agricultural cooperatives'], festivalNote: "Ugbenor women's cooperatives run a produce stall at the ERUE Festival market.", landmarks: ['Palm Oil Processing Centre', "Women's Cooperative Market"], color: '#1a6b3f' },
  'uluoke-uzairue':    { specialty: 'Elevated terrain and sentinel heritage', highlights: ['Elevated terrain identity', 'Sentinel and lookout heritage', 'Hillside farming traditions'], festivalNote: "Uluoke's panoramic hilltop is a scenic landmark visited by festival guests.", landmarks: ['Hilltop Lookout Point', 'Hillside Farm Terraces'], color: '#145f35' },
  'yelwa-uzairue':     { specialty: 'Commercial prosperity and trade-route heritage', highlights: ['Commercial prosperity', 'Trade-route heritage', 'Intercultural exchange tradition'], festivalNote: "Yelwa's vibrant market culture animates the ERUE Festival's commercial and cultural exchange programme.", landmarks: ['Yelwa Market', 'Trade Route Heritage Monument'], color: '#0e5c3a' },
};

const cardStyle: React.CSSProperties = {
  background: '#0E1812',
  border: '1px solid #1C2E22',
  borderRadius: 20,
  padding: 24,
};

export const CommunityDetailPage: React.FC<Props> = ({ slug, setActiveTab }) => {
  const [village,  setVillage]  = useState<Village | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    fetch('/api/portal/villages')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.villages) {
          const found = data.villages.find((v: Village) => v.slug === slug);
          if (found) { setVillage(found); } else { setNotFound(true); }
        } else { setNotFound(true); }
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  // ── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16, animation: 'pulse 2s infinite' }}>🏘️</div>
          <p style={{ color: 'rgba(237,232,220,0.45)', fontSize: 14, margin: 0 }}>
            Loading community profile...
          </p>
        </div>
      </div>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────
  if (notFound || !village) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 24px' }}>
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }} className="space-y-6">
          <div style={{ fontSize: 64 }}>🗺️</div>
          <h2 style={{ fontFamily: 'Cinzel, serif', color: '#EDE8DC', fontSize: 22, fontWeight: 900, margin: 0 }}>
            Community Not Found
          </h2>
          <p style={{ color: 'rgba(237,232,220,0.5)', fontSize: 14, margin: 0, lineHeight: 1.7 }}>
            The community{' '}
            <strong style={{ color: '#C9A84C' }}>
              {slug ? '"' + slug + '"' : 'requested'}
            </strong>{' '}
            was not found among the 22 villages and component communities of Uzairue Kingdom.
          </p>
          <button
            onClick={() => setActiveTab('communities')}
            style={{
              padding: '12px 28px', background: '#0F5132',
              border: '1px solid rgba(201,168,76,0.3)',
              color: '#C9A84C', fontWeight: 900, fontSize: 14,
              borderRadius: 12, cursor: 'pointer',
            }}
          >
            Back to All 22 Communities
          </button>
        </div>
      </div>
    );
  }

  const cultural  = CULTURAL_DATA[village.slug];
  const icon      = getCommunityIcon(village.slug);
  const color     = cultural?.color || '#0F5132';
  const isCapital = village.slug === 'jattu-uzairue';

  return (
    <div style={{ paddingBottom: 64 }} className="space-y-8">

      {/* Back nav */}
      <button
        onClick={() => setActiveTab('communities')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          color: '#C9A84C', fontSize: 13, fontWeight: 700,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 0,
        }}
      >
        Back to All 22 Communities
      </button>

      {/* Hero banner */}
      <section
        style={{
          background: 'linear-gradient(135deg, ' + color + 'dd, #070D0Aee)',
          border: '1px solid rgba(201,168,76,0.35)',
          borderRadius: 24, padding: '48px 32px', color: '#EDE8DC',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24 }}>
          <div style={{ fontSize: 72 }}>{icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <span style={{
                padding: '3px 12px', background: 'rgba(201,168,76,0.15)',
                border: '1px solid rgba(201,168,76,0.4)', borderRadius: 999,
                color: '#C9A84C', fontSize: 10, fontWeight: 900,
                textTransform: 'uppercase', letterSpacing: 2,
              }}>
                #{village.displayOrder} of 22
              </span>
              {isCapital && (
                <span style={{
                  padding: '3px 12px', background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999,
                  color: '#EDE8DC', fontSize: 10, fontWeight: 900,
                  textTransform: 'uppercase', letterSpacing: 2,
                }}>
                  👑 Royal Capital
                </span>
              )}
            </div>
            <h1 style={{
              fontFamily: 'Cinzel, serif', fontSize: 'clamp(26px,5vw,44px)',
              fontWeight: 900, color: '#C9A84C', margin: '0 0 8px',
            }}>
              {village.name}
            </h1>
            <p style={{ color: 'rgba(237,232,220,0.6)', fontSize: 13, margin: '0 0 4px' }}>
              Etsako West Local Government Area, Edo State, Nigeria
            </p>
            {cultural && (
              <p style={{ color: '#6EE7A0', fontSize: 13, fontWeight: 700, margin: 0 }}>
                {cultural.specialty}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Key facts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Community Leader', value: village.dauduOrLeader, sub: village.leaderTitle || 'The Okphe', icon: '🏛️' },
          { label: 'Est. Population',  value: village.populationEstimate, sub: 'residents',           icon: '👥' },
          { label: 'Festival Troupe',  value: village.activeTroupe,       sub: 'active cultural troupe', icon: '🎭' },
        ].map(f => (
          <div key={f.label} style={cardStyle}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>{f.icon}</div>
            <div style={{ fontSize: 10, color: 'rgba(237,232,220,0.35)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
              {f.label}
            </div>
            <div style={{ color: '#EDE8DC', fontWeight: 900, fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
              {f.value}
            </div>
            <div style={{ color: 'rgba(237,232,220,0.35)', fontSize: 11, marginTop: 2 }}>{f.sub}</div>
          </div>
        ))}
      </div>

      {/* Cultural significance */}
      <div style={{ ...cardStyle, borderRadius: 24, padding: 32 }}>
        <h2 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 17, fontWeight: 900, margin: '0 0 14px' }}>
          🌍 Cultural Significance
        </h2>
        <p style={{ color: 'rgba(237,232,220,0.75)', lineHeight: 1.8, margin: 0, fontSize: 14 }}>
          {village.culturalSignificance}
        </p>
      </div>

      {/* Heritage highlights */}
      {cultural?.highlights && (
        <div style={{ ...cardStyle, borderRadius: 24, padding: 32 }}>
          <h2 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 17, fontWeight: 900, margin: '0 0 18px' }}>
            Heritage Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {cultural.highlights.map((h, i) => (
              <div key={i} style={{ background: '#121E16', border: '1px solid #1C2E22', borderRadius: 12, padding: 16 }}>
                <div style={{ color: '#C9A84C', fontWeight: 700, fontSize: 13 }}>{h}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Landmarks */}
      {cultural?.landmarks && (
        <div style={{ ...cardStyle, borderRadius: 24, padding: 32 }}>
          <h2 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 17, fontWeight: 900, margin: '0 0 18px' }}>
            📍 Notable Landmarks
          </h2>
          <div className="space-y-3">
            {cultural.landmarks.map((lm, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#C9A84C', flexShrink: 0 }} />
                <span style={{ color: 'rgba(237,232,220,0.75)' }}>{lm}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Festival note */}
      {cultural?.festivalNote && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(15,81,50,0.25) 0%, rgba(7,13,10,0.95) 100%)',
          border: '1px solid rgba(15,81,50,0.4)',
          borderRadius: 24, padding: 32,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ fontSize: 36, flexShrink: 0 }}>🎊</div>
            <div>
              <h2 style={{ fontFamily: 'Cinzel, serif', color: '#6EE7A0', fontSize: 16, fontWeight: 900, margin: '0 0 10px' }}>
                Role in the Annual ERUE Festival
              </h2>
              <p style={{ color: 'rgba(237,232,220,0.75)', lineHeight: 1.8, margin: 0, fontSize: 14 }}>
                {cultural.festivalNote}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation footer */}
      <div style={{ ...cardStyle, borderRadius: 16, padding: 24, textAlign: 'center' }}>
        <p style={{ color: 'rgba(237,232,220,0.5)', fontSize: 13, margin: '0 0 16px', lineHeight: 1.7 }}>
          Uzairue Kingdom is home to{' '}
          <strong style={{ color: '#C9A84C' }}>22 villages and component communities</strong>,
          each with a distinct heritage and identity.
        </p>
        <button
          onClick={() => setActiveTab('communities')}
          style={{
            padding: '12px 32px', background: '#0F5132',
            border: '1px solid rgba(201,168,76,0.3)',
            color: '#C9A84C', fontWeight: 900, fontSize: 14,
            borderRadius: 12, cursor: 'pointer',
          }}
        >
          Explore All 22 Communities
        </button>
      </div>

    </div>
  );
};

export default CommunityDetailPage;
